import { useCallback, useMemo } from 'react';
import useToothPathologyDecays from './useToothPathologyDecays';
import { useDENTContext } from '../../../Services/DENTContext';
import { Tooth, ToothPathology } from '../../../../../graphql/graphql';
import useToothPathologyFractures from './useToothPathologyFractures';
import useToothPathologyToothWear from './useToothPathologyToothWear';
import useToothPathologyDiscoloration from './useToothPathologyDiscoloration';
import useToothPathologyApical from './useToothPathologyApical';
import useToothPathologyDevelopmentDisorder from './useToothPathologyDevelopmentDisorder';
import useToothPathologyRootDecay from './useToothPathologyRootDecay';

export default function useToothPathology() {
	const { activeTooth, setActiveTooth } = useDENTContext();
	const pathology = activeTooth?.pathology;

	const setPathology = useCallback(
		(pathology: ToothPathology) => {
			setActiveTooth((prev: Tooth | undefined) => {
				if (!prev) return prev;
				return { ...prev, pathology: pathology };
			});
		},
		[setActiveTooth],
	);

	const { decays } = useToothPathologyDecays({ pathology, setPathology });
	const { rootDecay } = useToothPathologyRootDecay({ pathology, setPathology });
	const { fractures } = useToothPathologyFractures({ pathology, setPathology });
	const { toothWear } = useToothPathologyToothWear({ pathology, setPathology });
	const { discoloration } = useToothPathologyDiscoloration({
		pathology,
		setPathology,
	});
	const { apical } = useToothPathologyApical({ pathology, setPathology });
	const { developmentDisorder } = useToothPathologyDevelopmentDisorder({
		pathology,
		setPathology,
	});

	return useMemo(
		() => ({
			pathology: {
				decays,
				rootDecay,
				fractures,
				toothWear,
				discoloration,
				apical,
				developmentDisorder,
			},
		}),
		[activeTooth, pathology],
	);
}
